import { useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { pickWallet, releaseWallet, useConnectedAccount, useWalletAccountTracking } from "../lib/connectedAccount";
import { errorReporter } from "../lib/errorReporter";
import { shortAddr } from "../lib/address";
import { getPortalUrl } from "../lib/links";
import { signInWithWallet } from "../lib/signIn";
import { useStateFrom } from "../lib/useStateFrom";
import {
  cancelConnect,
  explainWalletError,
  isConnectionCancelled,
  isReportableWalletError,
  isRequestPending,
  isUserRejected,
  NO_WALLET_MESSAGE,
  PENDING_MESSAGE,
  toWalletError,
  WALLETCONNECT_RDNS,
  WALLETCONNECT_UNAVAILABLE,
} from "../lib/wallet";
import { Brand } from "./Brand";
import { usePickedWallet, useSelectedRdns, useWalletProviders, walletOptions, WalletIcon, WalletPicker } from "./WalletPicker";

/** A line on the card: why the operator is back here. */
export interface SignInNotice {
  text: string;
  tone: "grey";
  /** Offered when the notice is a transient failure worth retrying as is. */
  retry?: () => void;
  /**
   * Drawn above the card's heading, whether or not the browser has a wallet,
   * with the usual wallet sign-in below it: the admin console's link refused,
   * or its session ended.
   */
  above?: boolean;
}

type Outcome = { tone: "red" | "amber"; text: string } | null;
type Phase = "idle" | "connecting" | "signing";

/** While WalletConnect loads or pairs: the QR sheet opens over the card. */
export const WALLETCONNECT_SCAN = "Scan the QR code with your wallet app, then approve the connection there.";

/** The sign-in's refusals, in the card's words. */
export const NOT_AN_OPERATOR = "This wallet is not an operator of a market maker on AileFX.";
export const SIGNATURE_REFUSED = "You refused the signature in your wallet. Nothing was signed.";
export const SIGN_IN_FAILED = "Sign-in failed. Please try again.";
export const NO_WALLET_TEXT = NO_WALLET_MESSAGE;
export const CONNECTION_REFUSED = "You refused the connection in your wallet. Nothing was shared.";
/** -32002: a request is already open in the wallet. Said, never reported. */
export const REQUEST_OPEN = PENDING_MESSAGE;

function BusySpinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true" className="signin-busy-spin">
      <circle cx="6" cy="6" r="4.5" stroke="#cfe6d6" />
      <path d="M6 1.5a4.5 4.5 0 0 1 4.5 4.5" stroke="#3f7a52" />
    </svg>
  );
}

/** A wallet failure in the card's words; reported only when `isReportableWalletError`. */
function walletOutcome(err: unknown, stage: "connect" | "sign-in"): Outcome {
  // The QR sheet closed, or Cancel: nothing was asked of any wallet, nothing to say.
  if (isConnectionCancelled(err)) return null;
  if (isUserRejected(err)) return { tone: "amber", text: stage === "connect" ? CONNECTION_REFUSED : SIGNATURE_REFUSED };
  if (isRequestPending(err)) return { tone: "amber", text: REQUEST_OPEN };
  if (!isReportableWalletError(err)) return { tone: "amber", text: explainWalletError(err) };
  const walletError = toWalletError(err);
  errorReporter.captureException(walletError, { source: "wallet", code: walletError.code, stage });
  // A WalletConnect failure carries its own finished words (what to do next).
  return { tone: "red", text: walletError.code === WALLETCONNECT_UNAVAILABLE ? explainWalletError(walletError) : SIGN_IN_FAILED };
}

/**
 * The only screen a visitor without a session sees: the operator picks one of
 * the browser's wallets or WalletConnect (one installed wallet is still a
 * choice, never picked for them), connects it and signs one message bound to
 * this site. The pick is remembered, and every later request goes to it only.
 */
export function SignInScreen({ notice, onSignedIn }: { notice: SignInNotice | null; onSignedIn: () => void }) {
  useWalletAccountTracking();
  const account = useConnectedAccount();
  const providers = useWalletProviders();
  const options = walletOptions(providers);
  const selectedRdns = useSelectedRdns();
  const picked = usePickedWallet(providers);
  const [phase, setPhase] = useState<Phase>("idle");
  // The connect in progress is WalletConnect's: its own hint, and Cancel.
  const [pairing, setPairing] = useState(false);
  // Another account in the wallet: whatever was said was about the previous one.
  const [outcome, setOutcome] = useStateFrom<Outcome>(null, account);
  const [noticeShown, setNoticeShown] = useState(true);
  const [choosing, setChoosing] = useState(false);
  const attempt = useRef(0);
  const portalUrl = getPortalUrl();

  const busy = phase !== "idle";
  const walletPresent = options.length > 0;
  // The account is always the picked wallet's (lib/connectedAccount.ts).
  const connected = account !== null && picked !== null ? { wallet: picked, account } : null;
  const showList = walletPresent && (connected === null || choosing);

  /**
   * A wallet from the list, straight from the click and nothing awaited
   * first: the wallet's popup needs the gesture. Remembered before it
   * connects, so a refusal still leaves it marked "Last used".
   */
  function pick(rdns: string) {
    if (busy) return;
    setOutcome(null);
    setChoosing(false);
    setPhase("connecting");
    setPairing(rdns === WALLETCONNECT_RDNS);
    const id = (attempt.current += 1);
    pickWallet(rdns).then(
      () => {
        if (attempt.current !== id) return;
        setPhase("idle");
        setPairing(false);
      },
      (err: unknown) => {
        if (attempt.current !== id) return;
        setPhase("idle");
        setPairing(false);
        setOutcome(walletOutcome(err, "connect"));
      },
    );
  }

  /** Cancel while WalletConnect loads or pairs: the sheet closes, the list is back, the pick stays. */
  function cancelPairing() {
    attempt.current += 1;
    cancelConnect();
    setPhase("idle");
    setPairing(false);
  }

  /** "Use another wallet": a WalletConnect pairing is hung up first (lib/connectedAccount.ts). */
  function chooseAnother() {
    setOutcome(null);
    releaseWallet();
    setChoosing(true);
  }

  function sign() {
    if (busy || connected === null) return;
    setOutcome(null);
    setNoticeShown(false);
    setPhase("signing");
    const id = (attempt.current += 1);
    signInWithWallet(connected.account).then(
      (result) => {
        if (attempt.current !== id) return;
        setPhase("idle");
        if (result.kind === "signed-in") onSignedIn();
        else setOutcome({ tone: "red", text: NOT_AN_OPERATOR });
      },
      (err: unknown) => {
        if (attempt.current !== id) return;
        setPhase("idle");
        if (err instanceof ApiError) {
          setOutcome({ tone: "red", text: err.status === 429 ? "Too many sign-in attempts. Wait a minute, then try again." : SIGN_IN_FAILED });
          return;
        }
        setOutcome(walletOutcome(err, "sign-in"));
      },
    );
  }

  const showNotice = notice !== null && noticeShown && outcome === null;
  // The admin console's line heads the card, and the usual sign-in follows.
  const noticeAbove = showNotice && notice.above === true;
  const hint =
    phase === "connecting" && pairing
      ? WALLETCONNECT_SCAN
      : phase === "signing"
      ? "Your wallet shows the message: it names the AileFX Maker portal and the network."
      : walletPresent && connected === null && (notice === null || notice.above === true) && outcome === null
        ? "Checked again on every page: when a wallet stops being an operator, its session ends."
        : null;

  return (
    <div className="signin-screen">
      <Brand name="AileFX Maker portal" sub="For market makers" />
      <section className="signin-card" aria-labelledby="signin-title">
        {noticeAbove && (
          <div className="signin-alert signin-alert-grey" role="status">
            {notice.text}
          </div>
        )}
        <div className="signin-heading">
          <h1 id="signin-title" className="signin-title">
            Sign in with your operator wallet
          </h1>
          <p className="signin-lead">
            AileFX names the wallets that may sign in for your company. You sign a message to show the wallet is yours: no transaction, no gas.
          </p>
        </div>

        {connected !== null && !choosing && (
          <div className="signin-wallet">
            <span className="signin-wallet-label">Connected wallet</span>
            <span className="signin-wallet-chip" title={connected.account}>
              <WalletIcon icon={connected.wallet.info.icon} className="signin-wallet-icon" />
              <span className="signin-wallet-name">{connected.wallet.info.name}</span>
              {shortAddr(connected.account)}
            </span>
            <span className="signin-wallet-sub">
              To sign in with another account, switch it in your wallet.{" "}
              <button type="button" className="signin-retry" onClick={chooseAnother} disabled={busy}>
                Use another wallet
              </button>
            </span>
          </div>
        )}

        {!walletPresent && (
          <div className="signin-alert signin-alert-grey" role="status">
            {NO_WALLET_TEXT}
          </div>
        )}
        {walletPresent && showNotice && !noticeAbove && (
          <div className="signin-alert signin-alert-grey" role="status">
            {notice.text}
            {notice.retry !== undefined && (
              <>
                {" "}
                <button type="button" className="signin-retry" onClick={notice.retry}>
                  Try again
                </button>
              </>
            )}
          </div>
        )}
        {outcome !== null && (
          <div className={`signin-alert signin-alert-${outcome.tone}`} role="alert">
            {outcome.text}
          </div>
        )}

        {showList && (
          <WalletPicker options={options} selectedRdns={selectedRdns} onPick={pick} disabled={busy} label="Choose your wallet" />
        )}
        {showList && connected !== null && !busy && (
          <button type="button" className="signin-retry signin-switch" onClick={() => setChoosing(false)}>
            Keep {connected.wallet.info.name}
          </button>
        )}

        {!walletPresent ? (
          <button type="button" className="signin-btn signin-btn-off" disabled>
            Connect browser wallet
          </button>
        ) : busy ? (
          <>
            <button type="button" className="signin-btn signin-btn-busy" disabled>
              <BusySpinner />
              Check your wallet…
            </button>
            {pairing && (
              <button type="button" className="signin-retry signin-switch" onClick={cancelPairing}>
                Cancel
              </button>
            )}
          </>
        ) : (
          connected !== null &&
          !choosing && (
            <button type="button" className="signin-btn" onClick={sign}>
              Sign the message
            </button>
          )
        )}

        {hint !== null && <p className="signin-note">{hint}</p>}
      </section>

      {portalUrl !== null && (
        <p className="signin-foot">
          Not a market maker? Your company&apos;s pages are on the{" "}
          <a href={portalUrl} target="_blank" rel="noreferrer noopener">
            client portal ↗
          </a>
        </p>
      )}
    </div>
  );
}
